import { faCheckCircle, faComments, faLock, faShoppingBasket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import lowerFirst from 'lodash-es/lowerFirst';
import {
    BAZAAR_CHECKOUT_USE_BETTER_SHIPPING_PRICE_BUTTON,
    BAZAAR_CHECKOUT_USE_BETTER_SHIPPING_PRICE_BUTTON_NOTE,
    BAZAAR_PRODUCT_IS_RESERVED,
    BZ2_CONTACT_MESSAGE_DIALOG_OPENER,
    BZ2_CONTACT_MESSAGE_WAS_SENT,
    BZ2_JUST_SEND_MESSAGE,
    BZ2_SEND_MESSAGE,
    BZ_CONTACT_OPEN_COMMUNICATION_WITH_SELLER,
    BZ_CONTACT_SEND_NEXT_MESSAGE,
    BZ_CONTACT_SHOW_COMMUNICATION_WITH_SELLER,
    BZ_CONTACT_SHOW_ORDER,
    BZ_CONTACT_WANT_TO_SEND_NEXT_MESSAGE,
} from 'mk/autogenerated/translations/ContactSellerForBuyer.5fed3a3bb73fb0e79b85bd09c0f3ca8e'
import { bazaar_redirect_to_eshop_url } from 'mk/urls/functions';
import MessageForm, { MessageFormData } from 'mk2/apps/bazaar/components/MessageForm';
import { ContactSellerActionsStep1 } from 'mk2/apps/bazaar/containers/ProductDetail/ContactSellerActionsStep1';
import { ContactSellerActionsStep2 } from 'mk2/apps/bazaar/containers/ProductDetail/ContactSellerActionsStep2';
import { isOpenBazaarOrder, slugToParams, CONTACT_SELLER_FORM } from 'mk2/apps/bazaar/helpers';
import { BazaarMessageEntity, BazaarOrderEntity, ProductEntity, ProductQuality } from 'mk2/apps/bazaar/schemas';
import { bazaarCheckoutUrl } from 'mk2/apps/bazaar/urls';
import { AvatarImg } from 'mk2/components/AvatarImg';
import { Btn, BtnSidePadding, BtnType } from 'mk2/components/Btn';
import { url } from 'mk2/helpers/urls';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { UserEntity } from 'mk2/schemas';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './ContactSellerForBuyer.mscss';
import NewFeatureCheckout from './NewFeatureCheckout';

interface OwnProps {
    product: ProductEntity;
    canOrder: boolean;
    // TODO:
    //    https://www.figma.com/file/fcIlNMvgw3nTuw9daZBaPH/Modry-konik-UI?type=design&node-id=9204-18207&mode=design&t=Ybz4T0PiacA7eUHR-0
    //    when is the product reserved? we have no field like product.stockAmount
    //    "Produkty prockarov mozu mat dokonca niekolko usepesnych objednavok naraz (lebo nove veci sa daju kupit viac krat)"
    isReserved?: boolean;
    canSendMsg: boolean;
    sellerPhone: string;
    eshopUrl: string;

    buyerUser: UserEntity;
    buyerMessages?: BazaarMessageEntity[];
    buyerOrders?: BazaarOrderEntity[];

    hasMessageFormInitiallyOpen: boolean;
    onSendMessage(productId: number, productSlug: string, values: MessageFormData);
    onScrollToMessages(event: React.MouseEvent<HTMLAnchorElement>);
}

export const ContactSellerForBuyer: React.FunctionComponent<OwnProps> = ({
    product,
    sellerPhone,
    eshopUrl,
    canOrder,
    isReserved,
    canSendMsg,

    buyerUser,
    buyerMessages = null,
    buyerOrders = null,
    hasMessageFormInitiallyOpen,

    onSendMessage, onScrollToMessages,
}) => {
    const [showMessagForm, setShowMessageForm] = useState<boolean>(hasMessageFormInitiallyOpen);

    const hasMessage = buyerMessages && buyerMessages.length > 0;
    const hasRecentMessage = hasMessage &&
        Date.now() - new Date(buyerMessages[buyerMessages.length - 1].sentDate).getTime() < 5 * 60 * 1000;  // 5min
    const hasOpenOrder = (buyerOrders || []).filter(isOpenBazaarOrder).length > 0;

    const handleOnSendMessage = useEventCallback((values: MessageFormData) => {
        onSendMessage(product.id, product.slug, values);
    }, [product, onSendMessage, setShowMessageForm]);

    useEffect(() => {
        // zavri message form, ak sa nam uspesne podarilo
        // pridat spravu (neboli form errors)
        if (hasRecentMessage && showMessagForm) {
            setShowMessageForm(false);
        }
    }, [hasRecentMessage, showMessagForm, setShowMessageForm]);

    const onCloseMessageForm = useEventCallback(() => {
        setShowMessageForm(false);
    }, [setShowMessageForm]);

    const onOpenMessageForm = useEventCallback(() => {
        setShowMessageForm(true);
    }, [setShowMessageForm]);

    const initialValues: MessageFormData = useMemo(() => ({
        message: '',
        photos: [],
    }), []);

    const orderBtn = canOrder ? (
        <>
            {isReserved && (
                <span className={styles.ContactSellerForBuyer__isReserved}>
                    <FontAwesomeIcon icon={faLock} />
                    &nbsp;
                    {BAZAAR_PRODUCT_IS_RESERVED.split('|').map((line, i) => <>
                        <span key={`line-${i}`}>{line}</span>
                        <br key={`br-${i}`} />
                    </>)}
                </span>
            )}
            <Btn
                link={url(bazaarCheckoutUrl, slugToParams(product.slug))}
                type={BtnType.Blue}
                label={BAZAAR_CHECKOUT_USE_BETTER_SHIPPING_PRICE_BUTTON}
                icon={faShoppingBasket}
                disabled={isReserved}
            />
            <span className={styles.ContactSellerForBuyer__checkoutNote}>
                {BAZAAR_CHECKOUT_USE_BETTER_SHIPPING_PRICE_BUTTON_NOTE}
            </span>
        </>
    ) : null;

    // poradie if-ov je dolezite, nie su mutually exclusive
    if (showMessagForm) {
        return (
            <div className={styles.ContactSellerForBuyer__messageForm}>
                <div className={styles.ContactSellerForBuyer__messageFormIntro}>
                    {BZ2_CONTACT_MESSAGE_DIALOG_OPENER}
                </div>
                <div className={styles.ContactSellerForBuyer__messageFormContainer}>
                    <AvatarImg
                        className={styles.ContactSellerForBuyer__messageFormAvatar}
                        user={buyerUser}
                        size={32}
                    />
                    <MessageForm
                        className={styles.ContactSellerForBuyer__messageFormInnerForm}
                        form={CONTACT_SELLER_FORM(product.id)}
                        initialValues={initialValues}
                        onCancel={onCloseMessageForm}
                        onSave={handleOnSendMessage}
                    />
                </div>
            </div>
        );
    } else if (hasOpenOrder) {
        const showOrderBtn = (
            <Btn
                type={BtnType.BlueOutline}
                label={BZ_CONTACT_SHOW_ORDER}
                onClick={onScrollToMessages}
            />
        );

        const showMsgsBtn = (
            <Btn
                type={BtnType.Link}
                sidePadding={BtnSidePadding.None}

                label={lowerFirst(BZ_CONTACT_OPEN_COMMUNICATION_WITH_SELLER)}
                onClick={onScrollToMessages}
            />
        );

        return (
            <ContactSellerActionsStep2
                orderBtn={showOrderBtn}
                showMsgsBtn={showMsgsBtn}
            />
        );
    } else if (hasMessage) {
        if (hasRecentMessage) {
            const orderBtnMuted = canOrder ? (
                <>
                    <Btn
                        link={url(bazaarCheckoutUrl, slugToParams(product.slug))}
                        type={BtnType.Link}
                        label={BAZAAR_CHECKOUT_USE_BETTER_SHIPPING_PRICE_BUTTON}
                        // icon={faShoppingBasket} do not show icon when muted
                        sidePadding={BtnSidePadding.None}
                    />
                    <span className={styles.ContactSellerForBuyer__checkoutNote}>
                        {BAZAAR_CHECKOUT_USE_BETTER_SHIPPING_PRICE_BUTTON_NOTE}
                    </span>
                </>
            ) : null;

            const showMsgsBtn = (
                <Btn
                    type={BtnType.Link}
                    sidePadding={BtnSidePadding.None}
                    label={canOrder
                        ? lowerFirst(BZ_CONTACT_SEND_NEXT_MESSAGE)
                        : BZ_CONTACT_WANT_TO_SEND_NEXT_MESSAGE
                    }
                    onClick={onScrollToMessages}
                />
            );

            return (
                <div className={styles.ContactSellerForBuyer__messageSent}>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        className={styles.ContactSellerForBuyer__messageSentIcon}
                    />
                    <p className={styles.ContactSellerForBuyer__messageSentText}>
                        {BZ2_CONTACT_MESSAGE_WAS_SENT}
                    </p>

                    <ContactSellerActionsStep2
                        orderBtn={orderBtnMuted}
                        showMsgsBtn={showMsgsBtn}
                        inline
                    />
                </div>
            );
        } else {
            const showMsgsBtn = canOrder ? (
                <Btn
                    type={BtnType.Link}
                    label={lowerFirst(BZ_CONTACT_SHOW_COMMUNICATION_WITH_SELLER)}
                    onClick={onScrollToMessages}
                    sidePadding={BtnSidePadding.None}
                />
            ) : (
                <Btn
                    type={BtnType.BlueOutline}
                    label={BZ_CONTACT_SHOW_COMMUNICATION_WITH_SELLER}
                    onClick={onScrollToMessages}
                />
            );

            return (
                <>
                    <div className={styles.ContactSellerForBuyer}>
                        {canOrder && <NewFeatureCheckout />}
                        <ContactSellerActionsStep2
                            orderBtn={orderBtn}
                            showMsgsBtn={showMsgsBtn}
                        />
                    </div>
                </>
            );
        }
    } else {

        const sendMsgBtnLabel = [ProductQuality.SERVICES, ProductQuality.JOBS].includes(product.quality)
        ? BZ2_JUST_SEND_MESSAGE
        : BZ2_SEND_MESSAGE;
        const sendMsgBtn = canSendMsg
            ? canOrder
                ? <Btn
                    type={BtnType.Link}
                    sidePadding={BtnSidePadding.None}
                    label={lowerFirst(sendMsgBtnLabel)}
                    onClick={onOpenMessageForm}
                    icon={faComments}

                /> : <Btn
                    type={BtnType.Blue}
                    label={sendMsgBtnLabel}
                    onClick={onOpenMessageForm}
                    icon={faComments}
                />
            : null;

        const phoneLink = sellerPhone ? (
            <Btn
                type={BtnType.Link}
                label={sellerPhone}
                link={`tel:${sellerPhone}`}
                linkTarget="_blank"
                linkRel="noopener"
                sidePadding={BtnSidePadding.None}
                asAnchor
            />
        ) : null;

        const eshopLink = eshopUrl ? (
            <Btn
                type={BtnType.Link}
                label={eshopUrl.replace(/^https?:\/\//, '').split('/')[0]}
                link={bazaar_redirect_to_eshop_url(product.id)}
                linkTarget="_blank"
                linkRel="noopener"
                sidePadding={BtnSidePadding.None}
                asAnchor
            />
        ) : null;

        return (
            <ContactSellerActionsStep1
                orderBtn={orderBtn}
                sendMsgBtn={sendMsgBtn}
                phoneLink={phoneLink}
                eshopLink={eshopLink}
            />
        );
    }
};
